import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { getStatusConfig } from '../config/statusConfig';
import { formatDate } from '../utils/dateUtils';
import { CONTENITORE_TABELLA, TESTATA_TABELLA } from './common/stiliTabella';

const TRIGGER_LABELS = { SCHEDULED: 'pianificata', MANUAL: 'manuale', UPLOAD: 'file' };
const COUNTERS = [
  ['rowsRead', 'Lette'],
  ['rowsInserted', 'Nuove'],
  ['rowsUpdated', 'Aggiornate'],
  ['rowsRejected', 'Scartate'],
];
const COLUMNS = 3 + COUNTERS.length;
const NUMBER_CELL = { fontVariantNumeric: 'tabular-nums' };

export const formatCount = (n) => (n == null ? '—' : String(n));

const rejectedLine = ({ line, key, errors }) =>
  `riga ${line}${key ? ` (${key})` : ''}: ${(errors || []).map((e) => `${e.field} — ${e.message}`).join('; ')}`;

export const RunStatusChip = ({ status }) => {
  const { label, color } = getStatusConfig('REGISTRY_SYNC', status);
  return <Chip size="small" variant="filled" color={color} label={label} />;
};

// Il conteggio vero viene da rowsRejected: il backend elenca nei dettagli solo le prime righe scartate.
const rejectedToggleLabel = (total, listed) => {
  const count = total === 1 ? '1 riga scartata' : `${total} righe scartate`;
  if (listed >= total) return count;
  return `${count} (${listed === 1 ? 'prima elencata' : `prime ${listed} elencate`})`;
};

/** Riga sotto un run fallito o parziale: il messaggio, leggibile e copiabile, e le righe scartate. */
const RunDetailRow = ({ run }) => {
  const [open, setOpen] = useState(false);
  const rejected = run.details?.rejected || [];
  const tone = run.status === 'FAILED' ? 'error.main' : 'warning.main';
  return (
    <TableRow>
      <TableCell colSpan={COLUMNS} sx={{ pt: 0, pl: 4 }}>
        {run.message && (
          <Typography variant="body2" color={tone}>{run.message}</Typography>
        )}
        {rejected.length > 0 && (
          <>
            <Button
              size="small"
              variant="text"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              sx={{ px: 0.5, minWidth: 0 }}
            >
              {rejectedToggleLabel(run.rowsRejected ?? rejected.length, rejected.length)}
            </Button>
            <Collapse in={open}>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {rejected.map((r) => (
                  <Typography component="li" variant="body2" key={`${r.line}-${r.key ?? ''}`}>
                    {rejectedLine(r)}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

const hasDetail = (run) => (run.status === 'FAILED' || run.status === 'PARTIAL')
  && (!!run.message || (run.details?.rejected?.length ?? 0) > 0);

/** Storico delle esecuzioni, dal piu' recente; paginazione lato server a dimensione fissa. */
const RegistrySyncRunsTable = ({ runs, totalElements, page, onPageChange, size, loading, error }) => {
  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={COLUMNS} align="center" sx={{ py: 3 }}>
            <CircularProgress size={24} aria-label="Caricamento esecuzioni" />
          </TableCell>
        </TableRow>
      );
    }
    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={COLUMNS}>
            <Typography variant="body2" color="error.main">{error}</Typography>
          </TableCell>
        </TableRow>
      );
    }
    return runs.map((run) => (
      <React.Fragment key={run.id}>
        <TableRow sx={hasDetail(run) ? { '& > td': { borderBottom: 'none' } } : undefined}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(run.startedAt)}</TableCell>
          <TableCell>{TRIGGER_LABELS[run.trigger] || run.trigger}</TableCell>
          <TableCell><RunStatusChip status={run.status} /></TableCell>
          {COUNTERS.map(([field]) => (
            <TableCell key={field} align="right" sx={NUMBER_CELL}>{formatCount(run[field])}</TableCell>
          ))}
        </TableRow>
        {hasDetail(run) && <RunDetailRow run={run} />}
      </React.Fragment>
    ));
  };

  return (
    <TableContainer {...CONTENITORE_TABELLA}>
      <Table size="small" aria-label="Esecuzioni">
        <TableHead>
          <TableRow sx={TESTATA_TABELLA}>
            <TableCell>Quando</TableCell>
            <TableCell>Avvio</TableCell>
            <TableCell>Stato</TableCell>
            {COUNTERS.map(([field, label]) => (
              <TableCell key={field} align="right">{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{renderBody()}</TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={(_e, next) => onPageChange(next)}
        rowsPerPage={size}
        rowsPerPageOptions={[size]}
        labelRowsPerPage="Righe per pagina"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} di ${count}`}
      />
    </TableContainer>
  );
};

export default RegistrySyncRunsTable;
